import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { User } from '../../../store/auth/auth.state';
import { selectAuthUser } from '../../../store/auth/auth.selectors';
import { Store } from '@ngrx/store';
import { ASSET_CONSTANTS } from '../../../shared/constants/asset-constants';
import { NzTableModule } from 'ng-zorro-antd/table';
import { FormsModule } from '@angular/forms';
import { NzPaginationComponent } from 'ng-zorro-antd/pagination';
import {
  MbButtonComponent,
  MbModalComponent,
  MbStatBoxComponent,
  MbSearchComponent,
  MbSkeletonComponent,
  ToastService,
} from '../../../components/common';
import {
  CitizensService,
  ICitizenStats,
} from '../../../core/services/citizen.services';
import { NewCitizenFormComponent } from '../../../components/forms';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-complaints-overview-page',
  imports: [
    CommonModule,
    NzTableModule,
    FormsModule,
    NzPaginationComponent,
    MbStatBoxComponent,
    MbButtonComponent,
    MbModalComponent,
    NewCitizenFormComponent,
    MbSearchComponent,
    MbSkeletonComponent,
    TranslocoDirective,
  ],
  templateUrl: './citizen-overview-page.component.html',
})
export class CitizenOverviewPageComponent implements OnInit {
  @ViewChild('newCitizenForm') newCitizenForm!: NewCitizenFormComponent;

  private store = inject(Store);
  private citizensService = inject(CitizensService);
  private transloco = inject(TranslocoService);
  private toastService = inject(ToastService);
  protected readonly ASSET_CONSTANTS = ASSET_CONSTANTS;
  protected user: User | null = null;
  protected isCitizenModalVisible = false;
  protected searchValue = '';

  // Pagination properties
  currentPage = 1;
  pageSize = 12;
  totalItems = 0;

  constructor(private router: Router) {}

  isLoadingCitizens = false;
  citizensError: string | null = null;
  listOfCitizens: ICitizenContent[] = [];
  citizenStats: ICitizenStats = {
    totalCitizens: 0,
    citizensWithOngoingCases: 0,
    averageCasesPerCitizen: 0,
    percentageFemales: 0,
    percentageMales: 0,
    percentageForeigners: 0,
  };

  ngOnInit(): void {
    this.loadCitizens(this.currentPage, this.pageSize, this.searchValue);
    this.loadCitizenStats();
    this.store.select(selectAuthUser).subscribe((user) => {
      this.user = user;
    });
  }

  navigateToCitizenDetail(id: string) {
    this.router.navigate(['/dashboard/citizens', id]);
  }

  loadCitizens(page: number, size: number, search = ''): void {
    this.isLoadingCitizens = true;
    this.citizensError = null;
    this.citizensService.getAllCitizens(page - 1, size, search).subscribe({
      next: (response) => {
        this.listOfCitizens = response.payload.content.map((citizen: any) => ({
          id: citizen.id || '',
          fullName: citizen.fullName || '',
          email: citizen.email || '',
          phoneNumber: citizen.phoneNumber || '',
          nationalIdNumber: citizen.nationalIdNumber || '',
          caseCount: citizen.caseCount || 0,
          createdAt: citizen.createdAt || '',
        }));
        this.totalItems = response.payload.page.totalElements;
        this.currentPage = page;
        this.pageSize = size;
        this.isLoadingCitizens = false;
      },
      error: (error) => {
        this.isLoadingCitizens = false;
        this.toastService.show(error.message || error.error.message, 'error');
      },
    });
  }

  loadCitizenStats(): void {
    this.citizensService.getCitizensStats().subscribe((response) => {
      this.citizenStats = {
        totalCitizens: response.payload.totalCitizens || 0,
        citizensWithOngoingCases:
          response.payload.citizensWithOngoingCases || 0,
        averageCasesPerCitizen: response.payload.averageCasesPerCitizen || 0,
        percentageFemales: response.payload.percentageFemales || 0,
        percentageMales: response.payload.percentageMales || 0,
        percentageForeigners: response.payload.percentageForeigners || 0,
      };
    });
  }

  get citizenStatsData(): { name: string; number: string }[] {
    return [
      {
        name: this.transloco.translate('totalCitizens'),
        number: this.citizenStats.totalCitizens?.toString() || '0',
      },
      {
        name: this.transloco.translate('withOngoingCases'),
        number: this.citizenStats.citizensWithOngoingCases?.toString() || '0',
      },
      {
        name: this.transloco.translate('avgCasesPerUser'),
        number: this.citizenStats.averageCasesPerCitizen?.toString() || '0',
      },
      {
        name: this.transloco.translate('female'),
        number: `${this.citizenStats.percentageFemales || 0}%`,
      },
      {
        name: this.transloco.translate('male'),
        number: `${this.citizenStats.percentageMales || 0}%`,
      },
      {
        name: this.transloco.translate('foreigners'),
        number: `${this.citizenStats.percentageForeigners || 0}%`,
      },
    ];
  }

  onSearch(): void {
    this.currentPage = 1;
    console.log(this.searchValue, this.currentPage, this.pageSize);
    this.loadCitizens(this.currentPage, this.pageSize, this.searchValue);
  }

  onClearSearch(): void {
    this.searchValue = '';
    this.loadCitizens(this.currentPage, this.pageSize);
  }

  onPageChange(page: number): void {
    this.loadCitizens(page, this.pageSize, this.searchValue);
  }

  onCitizenModalOkAction(): void {
    this.newCitizenForm.onSubmit();
  }

  onCitizenModalCancelAction(): void {
    this.isCitizenModalVisible = false;
  }

  onCitizenCreated(): void {
    this.loadCitizens(this.currentPage, this.pageSize, this.searchValue);
    this.isCitizenModalVisible = false;
  }
}

export interface ICitizenContent {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  nationalIdNumber: string;
  caseCount: number;
  createdAt: Date;
}
